import { ZoneFormCache, ZoneFormComponentProps, getLatestZone } from '../types'
import { Button } from 'components/Button/Button'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import SwitchStyled from 'pages/Robot/RobotView/RobotLeftBlock/RobotControl/SwitchStyled'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  selectMissionEditingZone,
  selectMissionInfo,
  selectMissionTempZone,
  selectSavingZoneProgress,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_SCRUBBER_BRUSH_SPEED,
  DEFAULT_TURBINE_SPEED,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
} from 'utils/constants'
import { ICleaningZone, RootState } from 'utils/interfaces'

import style from './ZoneDetails.module.css'

const applyEquipmentDefaults = (
  setTurbineSpeed: (val: number) => void,
  setFrontDustSuppression: (val: boolean) => void,
  setScrubberBrushSpeed: (val: number) => void,
  setCacheValue?: (key: keyof ZoneFormCache, value: any) => void
) => {
  setTurbineSpeed(DEFAULT_TURBINE_SPEED)
  setFrontDustSuppression(DEFAULT_FRONT_DUST_SUPPRESSION)
  setScrubberBrushSpeed(DEFAULT_SCRUBBER_BRUSH_SPEED)
  setCacheValue?.('turbineSpeed', DEFAULT_TURBINE_SPEED)
  setCacheValue?.('frontDustSuppression', DEFAULT_FRONT_DUST_SUPPRESSION)
  setCacheValue?.('scrubberBrushSpeed', DEFAULT_SCRUBBER_BRUSH_SPEED)
}

const ZoneEquipmentSettings = ({
  onSaveClicked,
  zone,
  getCacheValue,
  setCacheValue,
  getAllCacheValues,
}: ZoneFormComponentProps) => {
  const { t } = useTranslation()
  const editingZone = useSelector(selectMissionEditingZone)
  const tempZone = useSelector(selectMissionTempZone)
  const savingProgress = useSelector(selectSavingZoneProgress)
  const cleaningZones = useSelector((state: RootState) => state.mission.cleaningZones)
  const missionInfo = useSelector(selectMissionInfo)
  const isSweeper = missionInfo?.robot_type === 'Sweeper'

  const zoneToUse = editingZone || zone
  const latestZone = getLatestZone(cleaningZones, zoneToUse?.properties?.zone_id)
  const finalZone = latestZone || zoneToUse

  const getInitialValue = <K extends keyof ZoneFormCache>(key: K, defaultValue: ZoneFormCache[K]): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    if (cached !== undefined && cached !== null) {
      return cached
    }
    if ((key === 'turbineSpeed' || key === 'frontDustSuppression' || key === 'scrubberBrushSpeed') && finalZone) {
      const equipmentSetup = (finalZone as ICleaningZone)?.properties?.equipment_setup
      if (equipmentSetup) {
        if (key === 'turbineSpeed' && equipmentSetup.turbine_speed !== undefined) {
          return equipmentSetup.turbine_speed as ZoneFormCache[K]
        }
        if (key === 'frontDustSuppression' && equipmentSetup.front_dust_suppression !== undefined) {
          return equipmentSetup.front_dust_suppression as ZoneFormCache[K]
        }
        if (key === 'scrubberBrushSpeed' && equipmentSetup.scrubber_brush_speed !== undefined) {
          return equipmentSetup.scrubber_brush_speed as ZoneFormCache[K]
        }
      }
    }
    return defaultValue
  }

  const [turbineSpeed, setTurbineSpeed] = useState<number>(() => {
    return (getInitialValue('turbineSpeed', DEFAULT_TURBINE_SPEED) as number) || DEFAULT_TURBINE_SPEED
  })
  const [frontDustSuppression, setFrontDustSuppression] = useState<boolean>(() => {
    return (
      (getInitialValue('frontDustSuppression', DEFAULT_FRONT_DUST_SUPPRESSION) as boolean) ??
      DEFAULT_FRONT_DUST_SUPPRESSION
    )
  })
  const [scrubberBrushSpeed, setScrubberBrushSpeed] = useState<number>(() => {
    return (
      (getInitialValue('scrubberBrushSpeed', DEFAULT_SCRUBBER_BRUSH_SPEED) as number) || DEFAULT_SCRUBBER_BRUSH_SPEED
    )
  })

  useEffect(() => {
    setCacheValue?.('turbineSpeed', turbineSpeed)
    setCacheValue?.('frontDustSuppression', frontDustSuppression)
    setCacheValue?.('scrubberBrushSpeed', scrubberBrushSpeed)
  }, [turbineSpeed, frontDustSuppression, scrubberBrushSpeed, setCacheValue])

  const zoneIdRef = useRef<number | undefined>(zoneToUse?.properties?.zone_id)

  useEffect(() => {
    const currentZoneId = zoneToUse?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId

      const equipmentSetup = finalZone ? (finalZone as ICleaningZone)?.properties?.equipment_setup : null
      if (equipmentSetup) {
        const turbineSpeedValue =
          equipmentSetup.turbine_speed !== undefined && equipmentSetup.turbine_speed !== null
            ? equipmentSetup.turbine_speed
            : DEFAULT_TURBINE_SPEED
        const frontDustSuppressionValue =
          equipmentSetup.front_dust_suppression !== undefined && equipmentSetup.front_dust_suppression !== null
            ? equipmentSetup.front_dust_suppression
            : DEFAULT_FRONT_DUST_SUPPRESSION
        const scrubberBrushSpeedValue =
          equipmentSetup.scrubber_brush_speed !== undefined && equipmentSetup.scrubber_brush_speed !== null
            ? equipmentSetup.scrubber_brush_speed
            : DEFAULT_SCRUBBER_BRUSH_SPEED

        setTurbineSpeed(turbineSpeedValue)
        setFrontDustSuppression(frontDustSuppressionValue)
        setScrubberBrushSpeed(scrubberBrushSpeedValue)
        setCacheValue?.('turbineSpeed', turbineSpeedValue)
        setCacheValue?.('frontDustSuppression', frontDustSuppressionValue)
        setCacheValue?.('scrubberBrushSpeed', scrubberBrushSpeedValue)
      } else {
        applyEquipmentDefaults(setTurbineSpeed, setFrontDustSuppression, setScrubberBrushSpeed, setCacheValue)
      }
    }
  }, [zoneToUse?.properties?.zone_id, finalZone, setCacheValue])

  const savingProgressInWork = useMemo(() => {
    return savingProgress === PROGRESS_TYPES.WORK
  }, [savingProgress])

  const onTurbineSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === '') {
      setTurbineSpeed(DEFAULT_TURBINE_SPEED)
      setCacheValue?.('turbineSpeed', DEFAULT_TURBINE_SPEED)
      return
    }
    const value = parseFloat(inputValue)
    if (!isNaN(value)) {
      setTurbineSpeed(value)
      setCacheValue?.('turbineSpeed', value)
    }
  }

  const onScrubberBrushSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === '') {
      setScrubberBrushSpeed(DEFAULT_SCRUBBER_BRUSH_SPEED)
      setCacheValue?.('scrubberBrushSpeed', DEFAULT_SCRUBBER_BRUSH_SPEED)
      return
    }
    const value = parseFloat(inputValue)
    if (!isNaN(value)) {
      setScrubberBrushSpeed(value)
      setCacheValue?.('scrubberBrushSpeed', value)
    }
  }

  const getZoneFieldsFromCacheOrZone = () => {
    const cache = getAllCacheValues?.()
    const fallbackZone = tempZone || editingZone || zone
    return {
      zoneName: cache?.name || fallbackZone?.properties?.name || 'New Zone',
      zoneDescription: cache?.description || fallbackZone?.properties?.description || '',
      zoneType: (cache?.selectedZoneType ||
        fallbackZone?.properties?.zone_type ||
        MISSION_ZONE_TYPES.CLEANING) as MISSION_ZONE_TYPES,
      cleaningSpeed: cache?.cleaningSpeed,
      transportSpeed: cache?.transportSpeed,
      isBorderCleaning: cache?.isBorderCleaning,
    }
  }

  const onSave = () => {
    const equipment_setup = {
      turbine_speed: turbineSpeed,
      front_dust_suppression: frontDustSuppression,
      scrubber_brush_speed: scrubberBrushSpeed,
    }
    const zoneFields = getZoneFieldsFromCacheOrZone()

    onSaveClicked({
      ...zoneFields,
      equipment_setup,
    })
  }

  const onCancelClicked = () => {
    store.dispatch(missionThunks.cancelEditingZone())
  }

  const hasTurbineSpeedError = turbineSpeed < 300 || turbineSpeed > 3000
  const hasScrubberBrushSpeedError = scrubberBrushSpeed < 100 || scrubberBrushSpeed > 2000

  return (
    <div className={style.root}>
      <div className={style.content}>
        <TextFieldDescribed
          className={style.textfield}
          description="Turbine speed (RPM, 300-3000)"
          placeholder={DEFAULT_TURBINE_SPEED.toString()}
          type="number"
          value={turbineSpeed.toString()}
          onChange={onTurbineSpeedChange}
          hasError={hasTurbineSpeedError}
          error="Turbine speed must be between 300 and 3000 RPM"
        />
        {!isSweeper && (
          <TextFieldDescribed
            className={style.textfield}
            description="Scrubber brush speed (RPM, 100-2000)"
            placeholder={DEFAULT_SCRUBBER_BRUSH_SPEED.toString()}
            type="number"
            value={scrubberBrushSpeed.toString()}
            onChange={onScrubberBrushSpeedChange}
            hasError={hasScrubberBrushSpeedError}
            error="Scrubber brush speed must be between 100 and 2000 RPM"
          />
        )}
        <div className={style.tableLockRow}>
          <div className={style.textAlignLeft}>Front dust suppression</div>
          <div className={style.alignRight}>
            <SwitchStyled
              checked={frontDustSuppression}
              onChange={e => {
                const newValue = e.target.checked
                setFrontDustSuppression(newValue)
                setCacheValue?.('frontDustSuppression', newValue)
              }}
              disabled={false}
            />
          </div>
        </div>
      </div>
      <div className={style.buttons}>
        <Button.Outlined className={style.button} onClick={onCancelClicked} disabled={savingProgressInWork}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.button}
          onClick={onSave}
          disabled={hasTurbineSpeedError || (!isSweeper && hasScrubberBrushSpeedError) || savingProgressInWork}
          loading={savingProgressInWork}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </div>
  )
}

export default ZoneEquipmentSettings
